import moment from 'moment-timezone';

import HistoryLocationState from 'constants/HistoryState';
import defaultTimezone from 'constants/DefaultTimezone';
import { Arrangement } from 'store/rootState';
import { ScheduledEventShow } from 'store/eventState';

import { EventInitialValues, EventShow, EventShowPayload, IdentifiedArrangement, ShowPlaybackMode } from './Event.types';

export const playbackModesForIndex = (
  index: number,
  length: number,
): ShowPlaybackMode[] => {
  if (length === 1) {
    return [ShowPlaybackMode.LOOPING];
  }

  if (index === length - 1) {
    return [
      ShowPlaybackMode.REPETITION,
      ShowPlaybackMode.TIME,
      ShowPlaybackMode.LOOPING,
    ];
  }

  return [ShowPlaybackMode.REPETITION, ShowPlaybackMode.TIME];
};

const isBlank = (value: string) => value.trim() === '';

const withPlaybackDefaults = (show: EventShow): EventShow => {
  if (
    show.playbackMode === ShowPlaybackMode.REPETITION &&
    isBlank(show.repetition)
  ) {
    return { ...show, repetition: '1' };
  }

  if (show.playbackMode === ShowPlaybackMode.TIME && isBlank(show.time)) {
    return { ...show, time: '10' };
  }

  return show;
};

export const normalizeShowModes = (shows: EventShow[]): EventShow[] =>
  shows.map((show, index) => {
    const allowed = playbackModesForIndex(index, shows.length);
    const playbackMode = allowed.includes(show.playbackMode)
      ? show.playbackMode
      : allowed[0];

    return withPlaybackDefaults({ ...show, playbackMode });
  });

export const isIdentifiedArrangement = (
  arrangement: Arrangement,
): arrangement is IdentifiedArrangement => typeof arrangement.id === 'number';

export const arrangementToEventShow = (arrangement: { id: number }): EventShow => ({
  arrangementId: arrangement.id,
  playbackMode: ShowPlaybackMode.REPETITION,
  time: '',
  repetition: '',
});

export const appendSelectedArrangements = (
  shows: EventShow[],
  selectedArrangements: Array<{ id?: number }> = [],
): EventShow[] => {
  const existingIds = new Set(shows.map(show => show.arrangementId));
  const addedShows = selectedArrangements
    .filter((item): item is { id: number } => typeof item.id === 'number')
    .filter(item => !existingIds.has(item.id))
    .map(arrangementToEventShow);

  return normalizeShowModes([...shows, ...addedShows]);
};

export const eventFormToLocationValues = (values: EventInitialValues) => ({
  ...values,
  startDate: values.startDate?.utc().format(),
  startTime: values.startTime?.utc().format(),
  endDate: values.endDate?.utc().format(),
  endTime: values.endTime?.utc().format(),
  dateUntil: values.dateUntil ? new Date(values.dateUntil) : undefined,
});

export const restoreEventFormFromLocation = (
  locationState: HistoryLocationState | undefined,
  timeZone?: string,
): EventInitialValues | undefined => {
  if (!locationState?.fromEventFormFormValues) {
    return undefined;
  }

  const form = locationState.fromEventFormFormValues;

  return {
    ...form,
    shows: appendSelectedArrangements(
      form.shows || [],
      locationState.selectedArrangements,
    ),
    startDate: moment.tz(form.startDate, timeZone || defaultTimezone),
    startTime: moment.tz(form.startTime, timeZone || defaultTimezone),
    endDate: moment.tz(form.endDate, timeZone || defaultTimezone),
    endTime: moment.tz(form.endTime, timeZone || defaultTimezone),
    dateUntil: moment.tz(form.dateUntil, timeZone || defaultTimezone),
  };
};

const isPlaybackMode = (value?: string): value is ShowPlaybackMode =>
  Object.values(ShowPlaybackMode).includes(value as ShowPlaybackMode);

// Older payloads may omit playback_mode; infer it from duration vs repetition.
const playbackModeOf = (show: ScheduledEventShow): ShowPlaybackMode => {
  if (isPlaybackMode(show.playback_mode)) {
    return show.playback_mode;
  }

  return show.duration != null
    ? ShowPlaybackMode.TIME
    : ShowPlaybackMode.REPETITION;
};

const toFormNumber = (value?: number | null) =>
  value == null ? '' : String(value);

// Only arrangement_id (or an embedded arrangement.id) identifies a show.
// ScheduledEventShow.id is the show-row id and must not be used here.
const arrangementIdOf = (show: ScheduledEventShow): number | undefined => {
  if (typeof show.arrangement_id === 'number') {
    return show.arrangement_id;
  }

  if (typeof show.arrangement?.id === 'number') {
    return show.arrangement.id;
  }

  return undefined;
};

const toEventShow = (show: ScheduledEventShow): EventShow | null => {
  const arrangementId = arrangementIdOf(show);
  if (arrangementId === undefined) {
    return null;
  }

  return {
    arrangementId,
    playbackMode: playbackModeOf(show),
    time: toFormNumber(show.duration),
    repetition: toFormNumber(show.repetition),
  };
};

interface ScheduledEventShows {
  arrangements?: ScheduledEventShow[];
}

export const toEventShows = (
  scheduledEvent: ScheduledEventShows,
): EventShow[] => {
  if (!scheduledEvent.arrangements?.length) {
    return [];
  }

  const shows = scheduledEvent.arrangements
    .map(toEventShow)
    .filter((show): show is EventShow => show !== null);

  return normalizeShowModes(shows);
};

export const pendingEventShows = (
  pending_changes?: {
    arrangements?: ScheduledEventShow[];
  } | null,
): EventShow[] | undefined => {
  if (!pending_changes?.arrangements?.length) {
    return undefined;
  }

  return toEventShows({ arrangements: pending_changes.arrangements });
};

export const showsHavePendingApprovalHighlight = (
  pending_approval: boolean,
  pending_changes?: {
    arrangements?: ScheduledEventShow[];
  } | null,
): boolean =>
  Boolean(
    pending_approval &&
      (pending_changes?.arrangements?.length || !pending_changes),
  );

export const eventShowsToPayload = (shows: EventShow[]): EventShowPayload[] =>
  shows.map(show => ({
    arrangement_id: show.arrangementId,
    playback_mode: show.playbackMode,
    duration:
      show.playbackMode === ShowPlaybackMode.TIME ? Number(show.time) : null,
    repetition:
      show.playbackMode === ShowPlaybackMode.REPETITION
        ? Number(show.repetition)
        : null,
  }));
